import React, { useState, useEffect, useMemo } from "react";
import {
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  CircularProgress,
  TableRow,
  Tooltip,
  Typography,
  Card,
  Divider,
  CardContent,
  useMediaQuery,
  Link,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Pagination,
  Collapse,
  Stack,
  TableSortLabel,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Fade,
  Snackbar,
  ListItemAvatar,
  Avatar,
  Alert,
  FormControlLabel,
  Switch,
  Checkbox,
  InputLabel
} from "@mui/material";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../DatePickerStyles.css";
import {
  Email as EmailIcon,
  ContentCopy as ContentCopyIcon,
  Instagram as InstagramIcon,
  Person as PersonIcon,
  TabletAndroid as TabletAndroidIcon,
  FiberManualRecord as FiberManualRecordIcon,
  Update as UpdateIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import Dashboard from "./Dashboard";
import TabsSelector from "./TabsSelector";
import Conversations from "./Conversations";
import Logs from "./Logs";
import Settings from "./Settings";
import OutreachMessageGenerator from "./OutreachMessageGenerator";
import { useSearchParams, useNavigate } from "react-router-dom";
import DirectionIndicator from './DirectionIndicator';
import Appointments from "./Appointments";
import { AccessDeniedScreen } from "../utils/AccessDeniedScreen";
import { useCallback } from 'react';

const Hub = ({ setCurrentUser, currentUser, users, setUsers, updateSettings }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = Number(searchParams.get("page")) || 1;
  const initialSearch = searchParams.get("searchQuery") || "";
  const initialTab = searchParams.get("tab") || "dashboard";
  const initialStartDate = searchParams.get("startDate")
    ? new Date(searchParams.get("startDate"))
    : null;
  const initialEndDate = searchParams.get("endDate")
    ? new Date(searchParams.get("endDate"))
    : null;
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage] = useState(50);
  const [totalContactsCount, setTotalContactsCount] = useState(0);
  const [totalFollowersCount, setTotalFollowersCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [showWithoutIG, setShowWithoutIG] = useState(true);
  const [includeGrayNames, setIncludeGrayNames] = useState(true);
  const [statuses, setStatuses] = useState({});
  const [tab, setTab] = useState(initialTab);
  const [leadsSet, setLeadsSet] = useState(new Set());
  const [sortOrder, setSortOrder] = useState({ column: "date", order: "desc" });
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [loading, setLoading] = useState(true);
  const [delayedPage, setDelayedPage] = useState(page);
  const [delayedTab, setDelayedTab] = useState(tab);
  const [rowsVisible, setRowsVisible] = useState(true);
  const [interactionLog, setInteractionLog] = useState({});
  const [recentlyInteractedUsers, setRecentlyInteractedUsers] = useState(
    new Set(JSON.parse(localStorage.getItem("recentInteractions")) || [])
  );
  const isMdViewport = useMediaQuery("(max-width: 1080px)");
  const isMobile = useMediaQuery("(max-width: 800px)");
  const isMobileSm = useMediaQuery("(max-width: 420px)");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentContact, setCurrentContact] = useState(null);
  const [messageContent, setMessageContent] = useState("");
  const [currentConversation, setCurrentConversation] = useState(null);
  const [lastClickTime, setLastClickTime] = useState(null);
  const [instagramLinkDisabled, setInstagramLinkDisabled] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [setFilteredContacts] = useState([]);
  const [data, setData] = useState([]);
  const [setFilteredData] = useState([]);
  const [metricsData, setMetricsData] = useState([]);
  const [bookingsData, setBookingsData] = useState(null);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [conversations, setConversations] = useState([]);
  const token = localStorage.getItem("token");
  const [collapseOpen, setCollapseOpen] = useState(!isMobile);
  const [lastMessages, setLastMessages] = useState({});
  const [openPrompt, setOpenPrompt] = useState(false);
  const [pendingContactId, setPendingContactId] = useState(null);

  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState("");
  const [assignmentLoading, setAssignmentLoading] = useState(false);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/current-user`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch current user");
        }

        const result = await response.json();
        setCurrentUser(result.user);
      } catch (err) {
        console.error("Error fetching current user:", err);
      }
    };

    fetchCurrentUser();
  }, [tab]);

  useEffect(() => {
    if (isMdViewport) {
      setShowWithoutIG(true);
    }
  }, [isMdViewport]);

  const updateLastActive = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      await fetch(`${process.env.REACT_APP_API_BASE_URL}/update-last-active`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
    } catch (error) {
      console.error("Failed to update last active:", error);
    }
  }, []);

  useEffect(() => {
    const params = {};
    if (page !== 1) params.page = page;
    if (searchQuery) params.searchQuery = searchQuery;
    if (tab !== "dashboard") params.tab = tab;
    if (startDate) params.startDate = startDate.toISOString().slice(0, 10);
    if (endDate) params.endDate = endDate.toISOString().slice(0, 10);
    setSearchParams(params);
  }, [page, searchQuery, tab, startDate, endDate, setSearchParams]);

  useEffect(() => {
    setCollapseOpen(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    const fetchBookingsData = async () => {
      setLoadingBookings(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/bookings-count`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch bookings data");
        }

        const data = await response.json();
        setBookingsData(data.data);
      } catch (error) {
        console.error("Error fetching bookings data:", error);
      } finally {
        setLoadingBookings(false);
      }
    };

    fetchBookingsData();
  }, [token]);

  useEffect(() => {
    const loadMetrics = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/metrics`);
        if (!response.ok) {
          throw new Error("Failed to fetch contacts");
        }
        const metricsData = await response.json();
        setMetricsData(metricsData);
      } catch (error) {
        console.error(error);
      }
    };

    loadMetrics();
  }, []);

  useEffect(() => {
    const loadContacts = async () => {
      try {
        const params = new URLSearchParams({
          page: page,
          limit: rowsPerPage,
          searchQuery: searchQuery,
          ...(startDate && { startDate: startDate.toISOString().slice(0, 10) }),
          ...(endDate && { endDate: endDate.toISOString().slice(0, 10) }),
        });
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ghl/contacts?${params.toString()}`);
        if (!response.ok) {
          throw new Error("Failed to fetch contacts");
        }
        const contactsData = await response.json();
        setContacts(contactsData.data);
        setTotalContactsCount(contactsData.totalCount);
      } catch (error) {
        console.error(error);
      }
    };

    loadContacts();
  }, [page, searchQuery, startDate, endDate]);

  const [loadingFollowers, setLoadingFollowers] = useState(true);

  useEffect(() => {
    const loadFollowers = async () => {
      setLoadingFollowers(true);
      try {
        const params = new URLSearchParams({
          page: page,
          limit: rowsPerPage,
          searchQuery: searchQuery,
          ...(startDate && { startDate: startDate.toISOString().slice(0, 10) }),
          ...(endDate && { endDate: endDate.toISOString().slice(0, 10) }),
        });
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ghl/followers?${params.toString()}`);
        if (!response.ok) {
          throw new Error("Failed to fetch followers");
        }
        const followersData = await response.json();
        setData(followersData.data);
        setTotalFollowersCount(followersData.totalCount);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingFollowers(false);
      }
    };

    loadFollowers();
  }, [page, searchQuery, startDate, endDate]);

  useEffect(() => {
    const processContacts = () => {
      const leadUsernames = contacts
        .map((contact) => {
          const username = contact["IG Username"];
          return typeof username === "string" ? username.toLowerCase() : null;
        })
        .filter(Boolean);

      setLeadsSet(new Set(leadUsernames));
      setLoading(false);
    };

    processContacts();
  }, [contacts]);

  useEffect(() => {
    const logFromDatabase = {};
    users.forEach((user) => {
      Object.entries(user.interaction_log || {}).forEach(([username, timestamps]) => {
        logFromDatabase[username.toLowerCase()] = timestamps;
      });
    });

    setInteractionLog(logFromDatabase);
  }, [users]);

  useEffect(() => {
    if (leadsSet.size === 0) return;

    const newStatuses = {};
    contacts.forEach((contact) => {
      const username =
        typeof contact["IG Username"] === "string" ? contact["IG Username"].toLowerCase() : null;

      if (username) {
        if (leadsSet.has(username)) {
          newStatuses[username] = "Lead";
        }
      }
    });

    setStatuses(newStatuses);
  }, [leadsSet, contacts]);

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allUsernames = currentPaginatedData.map(row => {
        if (tab === "followers") {
          return row.string_list_data?.[0]?.value || "";
        } else {
          return row["IG Username"] || row.combinedName || "";
        }
      }).filter(Boolean);
      setSelectedUsers(new Set(allUsernames));
    } else {
      setSelectedUsers(new Set());
    }
  };

  const handleSelectUser = (username) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(username)) {
      newSelected.delete(username);
    } else {
      newSelected.add(username);
    }
    setSelectedUsers(newSelected);
  };

  const handleAssignUsers = async () => {
    if (!selectedAssigneeId || selectedUsers.size === 0) {
      setSnackbarMessage("Please select users and an assignee");
      setSnackbarOpen(true);
      return;
    }

    setAssignmentLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/assign-users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          assignedUserId: selectedAssigneeId,
          selectedUsernames: Array.from(selectedUsers),
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSnackbarMessage(result.message);
        setSnackbarOpen(true);
        setSelectedUsers(new Set());
        setAssignDialogOpen(false);
        setSelectedAssigneeId("");
      } else {
        setSnackbarMessage(result.message || "Failed to assign users");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error assigning users:", error);
      setSnackbarMessage("An error occurred while assigning users");
      setSnackbarOpen(true);
    } finally {
      setAssignmentLoading(false);
    }
  };

  const isIOSSafari = () => {
    return (
      /iP(ad|hone|od)/.test(navigator.userAgent) &&
      /Safari/.test(navigator.userAgent) &&
      !/CriOS/.test(navigator.userAgent)
    );
  };

  const openGHLContact = (contactId) => {
    if (isMobile) {
      setPendingContactId(contactId);
      setOpenPrompt(true);
    } else {
      window.open(
        `https://app.gohighlevel.com/v2/location/ZghV6U2wnAogcW6SRaAr/contacts/detail/${contactId}`,
        "_blank"
      );
    }
  };

  const handlePromptApp = () => {
    const contactId = pendingContactId;
    setOpenPrompt(false);

    const deepLink = `gohighlevel://contacts/detail/${contactId}`;
    const webLink = `https://app.gohighlevel.com/v2/location/ZghV6U2wnAogcW6SRaAr/contacts/detail/${contactId}`;

    if (!isIOSSafari()) {
      let didHide = false;
      const now = Date.now();

      const onBlur = () => {
        didHide = true;
        window.removeEventListener("blur", onBlur);
      };

      window.addEventListener("blur", onBlur);
      window.location.href = deepLink;

      setTimeout(() => {
        window.removeEventListener("blur", onBlur);
        if (!didHide && Date.now() - now < 1500) {
          window.location.href = webLink;
        }
      }, 3000);
    } else {
      window.location.href = deepLink;
    }
  };

  const handlePromptBrowser = () => {
    const contactId = pendingContactId;
    setOpenPrompt(false);
    window.location.href = `https://app.gohighlevel.com/v2/location/ZghV6U2wnAogcW6SRaAr/contacts/detail/${contactId}`;
  };

  const syncConversations = async (token) => {
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ghl/sync_conversations`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) throw new Error(`Sync Error: ${response.statusText}`);
    const result = await response.json();
    setConversations(result.data.conversations || []);
  };

  const filteredContacts = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return contacts.filter((contact) => {
      const username =
        typeof contact["IG Username"] === "string" ? contact["IG Username"].toLowerCase() : "";
      return username.includes(normalizedQuery);
    });
  }, [contacts, searchQuery]);

  const dateFilteredContacts = useMemo(() => {
    return filteredContacts.filter((contact) => {
      let createdDate = null;

      if (contact.Created) {
        if (typeof contact.Created === "string") {
          createdDate = new Date(contact.Created);
        } else if (contact.Created.$date) {
          createdDate = new Date(contact.Created.$date);
        }
      }

      if (startDate && createdDate && createdDate < startDate) return false;
      if (endDate && createdDate && createdDate > endDate) return false;

      return true;
    });
  }, [filteredContacts, startDate, endDate]);

  const displayedContacts = useMemo(() => {
    const combineName = (contact) => {
      const firstName = contact["First Name"] || "";
      const lastName = contact["Last Name"] || "";
      return `${firstName} ${lastName}`.trim();
    };

    if (tab === "leads") {
      const contactsForDisplay = showWithoutIG
        ? dateFilteredContacts
        : dateFilteredContacts.filter((contact) => contact["IG Username"]);

      return contactsForDisplay.map((contact) => ({
        ...contact,
        combinedName:
          contact["IG Username"] ||
          ((contact["First Name"] || contact["Last Name"])
            ? `${contact["First Name"] || ""} ${contact["Last Name"] || ""}`.trim()
            : contact["Phone"]) ||
          contact["Email"] ||
          "unknown",
      }));
    }

    if (tab === "leads") {
      return dateFilteredContacts.filter((contact) => {
        const username =
          typeof contact["IG Username"] === "string" ? contact["IG Username"].toLowerCase() : null;
      }).map((contact) => ({
        ...contact,
        combinedName: combineName(contact),
      }));
    }

    return dateFilteredContacts;
  }, [dateFilteredContacts, tab, leadsSet, showWithoutIG]);

  const handlePageChange = (event, value) => {
    setPage(value);
    setRowsVisible(false);
    setTimeout(() => {
      setDelayedPage(value);
      setRowsVisible(true);
    }, 300);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    updateLastActive();
    setRowsVisible(false);
    setSelectedUsers(new Set());
    setTimeout(() => {
      setDelayedTab(newValue);
      setDelayedPage(1);
      setPage(1);
      setRowsVisible(true);
    }, 300);
  };

  const handleSearchChange = (event) => {
    const newValue = event.target.value || "";
    setSearchQuery(newValue);
    setPage(1);
    setDelayedPage(1);
  };

  const handleCopy = (text) => {
    if (text) {
      navigator.clipboard.writeText(text);
    }
  };

  const handleLinkClick = (e, username, profileUrl) => {
    e.preventDefault();

    const currentTime = Date.now();

    if (lastClickTime && currentTime - lastClickTime < 7000) {
      const secondsRemaining = Math.ceil((7000 - (currentTime - lastClickTime)) / 1000);
      setSnackbarMessage(`You must wait ${secondsRemaining} seconds before opening another profile.`);
      setSnackbarOpen(true);
      return;
    }

    setLastClickTime(currentTime);
    setInstagramLinkDisabled(true);

    trackInteraction(username);

    const newTab = window.open();
    if (newTab) {
      newTab.opener = null;
      newTab.location = profileUrl;
    }

    setTimeout(() => {
      setInstagramLinkDisabled(false);
    }, 7000);
  };

  const trackInteraction = async (targetUsername) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("User token is missing");

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/track-interaction`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ targetUsername }),
      });

      if (!response.ok) {
        throw new Error("Failed to track interaction");
      }

      updateLastActive();

      setInteractionLog((prevLog) => ({
        ...prevLog,
        [targetUsername.toLowerCase()]: [new Date().toISOString()],
      }));
      setRecentlyInteractedUsers((prevSet) => {
        const newSet = new Set(prevSet).add(targetUsername.toLowerCase());
        localStorage.setItem("recentInteractions", JSON.stringify([...newSet]));
        return newSet;
      });
    } catch (error) {
      console.error("Error tracking interaction:", error);
    }
  };

  const handleSortToggle = (column) => {
    setSortOrder((prev) => {
      if (prev.column === column) {
        return { column, order: prev.order === "asc" ? "desc" : "asc" };
      }
      return { column, order: "asc" };
    });
  };

  const normalizeString = (str) => {
    return str.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
  };

  const showSnackbarMessage = (msg) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  };

  const filteredData = useMemo(() => {
    const normalizedQuery = normalizeString(searchQuery);
    return data.filter((row) => {
      const username = row.string_list_data[0]?.value || "";
      const normalizedUsername = normalizeString(username);
      return normalizedUsername.startsWith(normalizedQuery);
    });
  }, [data, searchQuery]);

  const dateFilteredData = useMemo(() => {
    return filteredData.filter((row) => {
      const followDate = row.string_list_data[0]?.timestamp
        ? new Date(row.string_list_data[0]?.timestamp * 1000)
        : null;
      if (startDate && followDate && followDate < startDate) return false;
      if (endDate && followDate && followDate > endDate) return false;
      return true;
    });
  }, [filteredData, startDate, endDate]);

  const displayedData = useMemo(() => {
    if (delayedTab === "followers") {
      return dateFilteredData;
    }
    if (delayedTab === "leads") {
      return dateFilteredData.filter((row) =>
        leadsSet.has(row.string_list_data[0]?.value?.toLowerCase())
      );
    }
    return dateFilteredData;
  }, [dateFilteredData, delayedTab, leadsSet]);

  const currentPaginatedData = useMemo(() => {
    if (tab === "leads" || tab === "inbox") {
      return displayedContacts;
    }
    return displayedData;
  }, [tab, displayedData, displayedContacts]);

  const totalPages = Math.ceil((tab === "followers" ? totalFollowersCount : totalContactsCount) / rowsPerPage);

  const handleYearShortcut = (year) => {
    setStartDate(new Date(`${year}-01-01`));
    setEndDate(new Date(`${year}-12-31`));
  };

  const handleOpenDialog = (contact) => {
    if (!conversations || !Array.isArray(conversations)) {
      setDialogOpen(true);
      setCurrentContact({ ...contact, conversation: null });
      return;
    }

    const conversation = conversations.find(
      (convo) => convo.contactId === contact["Contact Id"]
    );

    setCurrentContact(contact);
    setCurrentConversation(conversation || null);
    setDialogOpen(true);
  };

  const fetchLastMessages = async (contactIds) => {
    try {
      if (!contactIds || !contactIds.length) {
        console.warn('No valid contact IDs provided');
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/ghl/messages/get_last_messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ contactIds }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to fetch last messages: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      setLastMessages((prev) => ({ ...prev, ...result.data }));
    } catch (error) {
      console.error('Error fetching last messages:', error);
    }
  };

  useEffect(() => {
    if (displayedContacts.length > 0) {
      const contactIds = displayedContacts
        .filter(contact => contact && contact["Contact Id"])
        .map(contact => contact["Contact Id"]);

      if (contactIds.length > 0) {
        fetchLastMessages(contactIds);
      }
    }
  }, [displayedContacts]);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentContact(null);
    setCurrentConversation(null);
  };

  const handleSendMessage = async () => {
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const isAdmin = currentUser?.role === "admin";
  const isAllSelected = currentPaginatedData.length > 0 &&
    currentPaginatedData.every(row => {
      const username = tab === "followers"
        ? row.string_list_data?.[0]?.value
        : row["IG Username"] || row.combinedName;
      return selectedUsers.has(username);
    });

  const renderAssignButton = () => {
    if (!isAdmin || selectedUsers.size === 0) return null;

    return (
      <Box sx={{ p: 2, backgroundColor: "#1c1c1c" }}>
        <Button
          variant="contained"
          onClick={() => setAssignDialogOpen(true)}
          sx={{
            backgroundColor: "#98FF98",
            color: "#000",
            "&:hover": {
              backgroundColor: "#7dd87d",
            },
          }}
        >
          Assign Selected ({selectedUsers.size})
        </Button>
      </Box>
    );
  };

  const renderAssignmentDialog = () => (
    <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Users</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Assigning {selectedUsers.size} user(s) to:
        </Typography>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Select User</InputLabel>
          <Select
            value={selectedAssigneeId}
            onChange={(e) => setSelectedAssigneeId(e.target.value)}
            label="Select User"
          >
            {users
              .filter(user => user.role !== "admin")
              .map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.username} ({user.name || "No name"})
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="textSecondary">
            Selected users: {Array.from(selectedUsers).join(", ")}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAssignDialogOpen(false)} disabled={assignmentLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleAssignUsers}
          variant="contained"
          disabled={assignmentLoading || !selectedAssigneeId}
          sx={{
            backgroundColor: "#98FF98",
            color: "#000",
            "&:hover": {
              backgroundColor: "#7dd87d",
            },
          }}
        >
          {assignmentLoading ? <CircularProgress size={20} /> : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (
    !currentUser ||
    currentUser.role === "user"
  ) {
    return (
      <AccessDeniedScreen />
    )
  }

  return (
    <Box>
      <Fade in={!loading} timeout={500}>
        <Box sx={{ paddingTop: isMobile ? "16px" : "0px" }}>
          <Box>
            <TabsSelector value={tab} onChange={handleTabChange} settings={currentUser?.settings} />
            {tab === "dashboard" ? (
              <Box>
                <Dashboard
                  usersData={users}
                  setUsersData={setUsers}
                  metricsData={metricsData}
                  setMetricsData={setMetricsData}
                  bookingsData={bookingsData}
                  loadingBookings={loadingBookings}
                />
              </Box>
            ) : tab === "assigned" ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                <CircularProgress size={50} sx={{ color: "#98FF98" }} />
              </Box>
            ) : tab === "inbox" ? (
              <Conversations
                conversations={conversations}
                currentUser={currentUser}
                syncConversations={syncConversations}
              />
            ) : tab === "logs" ? (
              <Logs />
            ) : tab === "appointments" ? (
              <Appointments />
            ) : tab === "settings" ? (
              <Settings currentUser={currentUser} updateSettings={updateSettings} />
            ) : (
              <Paper sx={{
                padding: "8px", backgroundColor: "#101010", borderTopLeftRadius: "8px", borderTopRightRadius: "8px", borderBottomLeftRadius: "0px", borderBottomRightRadius: "0px"
              }}>
                {isMobile && (
                  <Box sx={{
                    justifyContent: "center",
                    display: "flex",
                    alignItems: "center",
                    pt: "8px",
                    pr: "20px",
                    pl: "18px"
                  }}>
                    <Button onClick={() => setCollapseOpen(prev => !prev)} variant="contained" size="small" sx={{
                      width: isMobile ? "100%" : "350px",
                      height: "40px",
                      marginBottom: collapseOpen ? "16px" : "0px",
                      border: "1px solid #333",
                      backgroundColor: "#202020",
                      color: "#ddd",
                      textShadow: "0px 0px 4px #777",
                      "&:hover": {
                        color: "#DDD",
                        backgroundColor: "#232323"
                      },
                    }}>
                      {collapseOpen ? "Collapse" : "Set Filters"}
                    </Button>
                  </Box>
                )}
                <Collapse in={collapseOpen} sx={{
                  pr: isMobile ? "20px" : "0px", pl: isMobile ? "18px" : "0px"
                }}>
                  <TextField
                    label={<><SearchIcon sx={{ mb: "-4px" }} /> Search Usernames</>}
                    variant="outlined"
                    fullWidth
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{ backgroundColor: "#222" }}
                    InputLabelProps={{
                      sx: {
                        color: "#777",
                        "&.Mui-focused": {
                          color: "#98FF98",
                        },
                        "&.MuiFormLabel-filled": {
                          color: "#98FF98",
                        },
                      },
                    }}
                    InputProps={{
                      style: { color: "#98FF98" },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#333",
                        },
                        "&:hover fieldset": {
                          borderColor: "#555",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#555",
                        },
                      },
                    }}
                  />
                  <Box display="flex" flexDirection={isMobile ? "column" : "row"} gap="16px" marginTop="16px">
                    {isMobile ? (
                      <Box>
                        <FormControl fullWidth size="small" variant="outlined">
                          <Select
                            value=""
                            onChange={(e) => handleYearShortcut(e.target.value)}
                            displayEmpty
                            sx={{
                              backgroundColor: "#   ",
                              color: "#9e9e9e",
                              "& .MuiSelect-select": {
                                padding: "8px",
                              },
                            }}
                          >
                            <MenuItem disabled value="">
                              Sort By Year
                            </MenuItem>
                            {[2021, 2022, 2023, 2024, 2025].map((year) => (
                              <MenuItem key={year} value={year}>
                                {year}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    ) : null}

                    <Box display="flex" flexShrink={0} sx={{ display: "flex", alignItems: "center", width: isMobile ? "100%" : "300px" }}>
                      <Box sx={{ marginRight: "10px" }}>
                        <ReactDatePicker
                          selected={startDate}
                          onChange={(date) => setStartDate(date)}
                          dateFormat="yyyy/MM/dd"
                          isClearable
                          placeholderText="Select Start Date"
                          className="custom-datepicker-input"
                          popperClassName="custom-datepicker-popper"
                        />
                      </Box>
                      <ReactDatePicker
                        selected={endDate}
                        onChange={(date) => setEndDate(date)}
                        dateFormat="yyyy/MM/dd"
                        isClearable
                        placeholderText="Select End Date"
                        className="custom-datepicker-input"
                        popperClassName="custom-datepicker-popper"
                      />
                    </Box>

                    {!isMobile && (
                      <Box display="flex" gap="8px" flexShrink={0}>
                        {[2021, 2022, 2023, 2024, 2025].map((year) => (
                          <Button
                            key={year}
                            variant="outlined"
                            size="small"
                            sx={{
                              height: "33px",
                              color: "#ccc",
                              borderColor: "#333",
                              backgroundColor: "#232323",
                              "&:hover": {
                                backgroundColor: "#2a2a2a"
                              },
                            }}
                            onClick={() => handleYearShortcut(year)}
                          >
                            {year}
                          </Button>
                        ))}
                      </Box>
                    )}
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeGrayNames}
                          onChange={(e) => setIncludeGrayNames(e.target.checked)}
                          color="success"
                        />
                      }
                      label="Include Gray Names"
                      sx={{ margin: 0, marginLeft: isMobile ? "-10px" : "0px", display: tab === "followers" ? "block" : "none" }}
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={showWithoutIG}
                          onChange={(e) => setShowWithoutIG(e.target.checked)}
                          color="success"
                        />
                      }
                      label="Include Leads Without IG"
                      sx={{ margin: 0, display: tab === "followers" ? "none" : isMdViewport ? "none" : "block" }}
                    />
                  </Box>
                </Collapse>
              </Paper>
            )}
            {!["dashboard", "inbox", "assigned", "logs", "appointments", "settings"].includes(tab) && !isMobile && (
              <OutreachMessageGenerator
                showSnackbar={showSnackbarMessage}
                handleCopy={handleCopy}
              />
            )}
          </Box>
          {!["dashboard", "inbox", "assigned", "logs", "appointments", "settings"].includes(tab) && (
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                backgroundColor: "#1c1c1c",
                padding: "8px 8px",
                paddingBottom: "12px",
                display: "flex",
                justifyContent: "center",
                alignItems: "left"
              }}
            >
              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, value) => {
                  if (value !== page) handlePageChange(event, value);
                }}
                siblingCount={isMobile ? 0 : isMdViewport ? 3 : 6}
                boundaryCount={isMobile ? 1 : 2}
                variant="outlined"
                shape="rounded"
                size={isMobile ? "medium" : "large"}
              />
            </Box>
          )}
        </Box>
      </Fade>
      {!["dashboard", "inbox", "assigned", "logs", "appointments", "settings"].includes(tab) && (
        <Box>
          {renderAssignButton()}
          {loadingFollowers ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
              <CircularProgress size={50} sx={{ color: "#98FF98" }} />
            </Box>
          ) : (
            <Fade in={!loading} timeout={500}>
              {isMobile ? (
                <TableContainer
                  className="table-container"
                  sx={{
                    paddingTop: "16px",
                    height: "calc(100vh - 200px)",
                    backgroundColor: "#1a1a1a",
                    borderBottomLeftRadius: "8px",
                    borderBottomRightRadius: "8px",
                  }}
                >
                  <Stack spacing={2} sx={{ px: "18px" }}>
                    {currentPaginatedData.map((row, index) => {
                      let username, profileUrl, createdDate;
                      const displayName =
                        tab === "followers" || tab === "leads" ? row.combinedName || "N/A" : row["IG Username"] || "unknown";
                      const hasIGUsername =
                        tab === "followers"
                          ? Boolean(row.string_list_data && row.string_list_data[0]?.value)
                          : Boolean(row["IG Username"]);

                      if (tab === "followers") {
                        if (!row.string_list_data || !Array.isArray(row.string_list_data)) {
                          return null;
                        }
                        username = row.string_list_data[0]?.value || "unknown";
                        profileUrl = row.string_list_data[0]?.href || "#";
                        createdDate = row.string_list_data[0]?.timestamp
                          ? new Date(row.string_list_data[0].timestamp * 1000).toLocaleDateString()
                          : "Unknown";
                      } else {
                        username = row.combinedName || row["IG Username"] || "unknown";
                        profileUrl = row["IG Username"]
                          ? `https://www.instagram.com/${row["IG Username"]}`
                          : "#";

                        if (row.Created) {
                          if (typeof row.Created === "string") {
                            createdDate = new Date(row.Created).toLocaleDateString();
                          } else if (row.Created.$date) {
                            createdDate = new Date(row.Created.$date).toLocaleDateString();
                          } else {
                            createdDate = "Unknown";
                          }
                        } else {
                          createdDate = "Unknown";
                        }
                      }

                      const lastMsg = lastMessages[row["Contact Id"]];
                      let lastMsgDate = lastMsg ? new Date(lastMsg.timestamp).toLocaleDateString() : "";
                      let direction = lastMsg ? lastMsg.direction : null;

                      const latestInteractionDate = interactionLog[username]?.[0] || null;
                      const isGrayedOut = tab === "followers" ? !!latestInteractionDate : row.alreadyMsged;

                      if (!includeGrayNames && isGrayedOut) return null;

                      const isSelected = selectedUsers.has(username);

                      return (
                        <Fade key={index} in={rowsVisible} timeout={300}>
                          <Card
                            key={index}
                            variant="contained"
                            sx={{
                              backgroundColor: isGrayedOut ? "#141414" : "#212121",
                              opacity: isGrayedOut ? 0.1 : 1,
                              transition: "opacity 0.3s ease",
                            }}
                          >
                            <CardContent>
                              <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography
                                  variant="body2"
                                  gutterBottom
                                  sx={{
                                    color: isGrayedOut ? "rgba(170, 170, 170, 0.5)" : "#98FF98",
                                    opacity: isGrayedOut ? 0.5 : 1,
                                    transition: "opacity 0.3s ease",
                                    cursor: "pointer",
                                    fontSize: 14,

                                    flex: 1
                                  }}
                                  onClick={(e) => {
                                    if (hasIGUsername) {
                                      handleLinkClick(e, username, profileUrl);
                                    } else {
                                      openGHLContact(row["Contact Id"]);
                                    }
                                  }}
                                  disabled={instagramLinkDisabled || !hasIGUsername}
                                >
                                  {username}
                                </Typography>
                                {isAdmin && (
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => handleSelectUser(username)}
                                    sx={{ color: "#98FF98" }}
                                  />
                                )}
                              </Box>
                              <Box display="flex" alignItems="center" mt={1}>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: isGrayedOut ? "rgba(255, 255, 255, 0.3)" : "#b5b5b5",
                                    filter: isGrayedOut ? "brightness(0.7)" : "none",
                                    marginRight: 1.5,
                                  }}
                                >
                                  <UpdateIcon sx={{ marginBottom: "-3px", fontSize: "1rem", mr: "3px" }} />{createdDate}
                                </Typography>
                                {direction && (
                                  <DirectionIndicator direction={direction} />
                                )}
                                <Typography variant="caption" sx={{ color: "#b5b5b5", mr: "3px" }}>
                                  {lastMsgDate}
                                </Typography>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <IconButton
                                  href={profileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label="Instagram Profile"
                                  sx={{
                                    color: isGrayedOut ? "rgba(255, 255, 255, 0.3)" : "#9e9e9e",
                                    filter: isGrayedOut ? "brightness(0.5)" : "none",
                                  }}
                                  disabled={instagramLinkDisabled || !hasIGUsername}
                                  onMouseDown={hasIGUsername ? (e) => handleLinkClick(e, username, profileUrl) : null}
                                >{hasIGUsername ? (
                                  <InstagramIcon sx={{ color: isGrayedOut ? "rgba(255, 255, 255, 0.3)" : "#98FF98", fontSize: 20, ml: "-9px" }} />
                                ) : (<InstagramIcon sx={{ color: "#777", fontSize: 20, ml: "-9px" }} />)}
                                </IconButton>
                                <Tooltip
                                  title={
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCopy(row.Phone);
                                        }}
                                        sx={{ color: "#98FF98", display: row.Phone ? "flex" : "none" }}
                                      >
                                        <ContentCopyIcon fontSize="small" />
                                      </IconButton>
                                      {row.Phone ? row.Phone : "Phone # Not Collected"}
                                    </Box>
                                  }
                                  slotProps={{
                                    popper: {
                                      modifiers: [
                                        {
                                          name: "preventOverflow",
                                          options: {
                                            boundary: "window",
                                          },
                                        },
                                      ],
                                    },
                                    tooltip: {
                                      sx: {
                                        color: row.Phone ? "#FDFDFD" : "#777",
                                        fontSize: "1.1rem",
                                        padding: "12px 16px",
                                        backgroundColor: "#343434",
                                        borderRadius: "8px",
                                      },
                                    },
                                  }}
                                >
                                  {row.Phone ? (
                                    <TabletAndroidIcon sx={{ marginRight: 1.25, color: "#98FF98", fontSize: 16, display: tab === "leads" ? "flex" : "none" }} />
                                  ) : (
                                    <TabletAndroidIcon sx={{ marginRight: 1.25, color: "#777", display: tab === "leads" ? "flex" : "none", fontSize: 16 }} />
                                  )}
                                </Tooltip>

                                <Tooltip
                                  title={
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCopy(row.Email);
                                        }}
                                        sx={{
                                          color: "#98FF98", display: row.Email && !row.Email.includes("@temp.com") ? "flex" : "none"
                                        }}
                                      >
                                        <ContentCopyIcon fontSize="small" />
                                      </IconButton>
                                      {row.Email && !row.Email.includes("@temp.com") ? row.Email : "Email Not Collected"}
                                    </Box>
                                  }
                                  slotProps={{
                                    popper: {
                                      modifiers: [
                                        {
                                          name: "preventOverflow",
                                          options: {
                                            boundary: "window",
                                          },
                                        },
                                      ],
                                    },
                                    tooltip: {
                                      sx: {
                                        color: row.Email ? "#FDFDFD" : "#777",
                                        fontSize: "1.1rem",
                                        padding: "12px 16px",
                                        backgroundColor: "#343434",
                                        borderRadius: "8px",
                                      },
                                    },
                                  }}
                                >
                                  {row.Email ? (
                                    <EmailIcon sx={{ color: "#98FF98", fontSize: 20, display: tab === "leads" ? "flex" : "none" }} />
                                  ) : (
                                    <EmailIcon sx={{ color: "#777", fontSize: 20, display: tab === "leads" ? "flex" : "none" }} />
                                  )}
                                </Tooltip>
                              </Box>
                            </CardContent>
                          </Card>
                        </Fade>
                      );
                    })}
                  </Stack>
                </TableContainer>
              ) : (
                <Paper>
                  <TableContainer
                    className="table-container"
                    sx={{
                      height: "calc(100vh - 360px)",
                      borderBottomLeftRadius: "8px",
                      borderBottomRightRadius: "8px",
                      overflowY: "auto",
                    }}
                  >
                    <Table>
                      <TableHead sx={{ backgroundColor: "#191919" }}>
                        <TableRow>
                          {isAdmin && (
                            <TableCell sx={{ width: "5%" }}>
                              <Checkbox
                                checked={isAllSelected}
                                indeterminate={selectedUsers.size > 0 && !isAllSelected}
                                onChange={handleSelectAll}
                                sx={{ color: "#9e9e9e" }}
                              />
                            </TableCell>
                          )}
                          {tab === "leads" && (
                            <TableCell sx={{ width: "20%", color: "#9e9e9e" }}>
                              <TableSortLabel
                                active={sortOrder.column === "lastMessage"}
                                direction={sortOrder.order}
                                onClick={() => handleSortToggle("lastMessage")}
                                sx={{ color: "#9e9e9e" }}
                              >
                                Last Message
                              </TableSortLabel>
                            </TableCell>
                          )}
                          <TableCell sx={{ width: "30%" }}>
                            <TableSortLabel
                              active={sortOrder.column === "username"}
                              direction={sortOrder.order}
                              onClick={() => handleSortToggle("username")}
                              sx={{ color: "#9e9e9e", ml: isAdmin ? "0px" : "62px" }}
                            >
                              {tab === "followers" || tab === "leads" ? "Name" : "Username"}
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ width: "20%" }}>
                            <TableSortLabel
                              active={sortOrder.column === "date"}
                              direction={sortOrder.order}
                              onClick={() => handleSortToggle("date")}
                              sx={{ color: "#9e9e9e" }}
                            >
                              Date
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ width: "10%", color: "#9e9e9e", }}>Instagram</TableCell>
                          <TableCell sx={{ width: "10%", color: "#9e9e9e", display: tab === "followers" ? "none" : "" }}>Phone</TableCell>
                          <TableCell sx={{ width: "10%", color: "#9e9e9e", display: tab === "followers" ? "none" : "" }}>Email</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentPaginatedData.map((row, index) => {
                          let username, profileUrl, createdDate;
                          const displayName =
                            tab === "followers" || tab === "leads" ? row.combinedName || "N/A" : row["IG Username"] || "unknown";
                          const hasIGUsername =
                            tab === "followers"
                              ? Boolean(row.string_list_data && row.string_list_data[0]?.value)
                              : Boolean(row["IG Username"]);

                          if (tab === "followers") {
                            if (!row.string_list_data || !Array.isArray(row.string_list_data)) {
                              return null;
                            }
                            username = row.string_list_data[0]?.value || "unknown";
                            profileUrl = row.string_list_data[0]?.href || "#";
                            createdDate = row.string_list_data[0]?.timestamp
                              ? new Date(row.string_list_data[0].timestamp * 1000).toLocaleDateString()
                              : "Unknown";
                          } else {
                            username = row.combinedName || row["IG Username"] || "unknown";
                            profileUrl = row["IG Username"]
                              ? `https://www.instagram.com/${row["IG Username"]}`
                              : "#";

                            if (row.Created) {
                              if (typeof row.Created === "string") {
                                createdDate = new Date(row.Created).toLocaleDateString();
                              } else if (row.Created.$date) {
                                createdDate = new Date(row.Created.$date).toLocaleDateString();
                              } else {
                                createdDate = "Unknown";
                              }
                            } else {
                              createdDate = "Unknown";
                            }
                          }

                          const lastMsg = lastMessages[row["Contact Id"]];
                          let lastMsgDate = lastMsg ? new Date(lastMsg.timestamp).toLocaleDateString() : "N/A";
                          let direction = lastMsg ? lastMsg.direction : null;

                          const latestInteractionDate = interactionLog[username]?.[0] || null;
                          const isGrayedOut = tab === "followers" ? !!latestInteractionDate : row.alreadyMsged;

                          if (!includeGrayNames && isGrayedOut) return null;

                          const isSelected = selectedUsers.has(username);

                          return (
                            <Fade key={index} in={rowsVisible} timeout={300}>
                              <TableRow
                                key={index}
                                hover
                                sx={{
                                  backgroundColor: isGrayedOut ? "#151515" : "#1e1e1e",
                                  opacity: isGrayedOut ? 0.1 : 1,
                                  transition: "opacity 0.3s ease",
                                  "&:hover": {
                                    backgroundColor: isGrayedOut ? "#0f0f0f" : "#181818",
                                  }
                                }}
                              >
                                {isAdmin && (
                                  <TableCell>
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={() => handleSelectUser(username)}
                                      sx={{ color: "#98FF98" }}
                                    />
                                  </TableCell>
                                )}
                                {tab === "leads" && (
                                  <TableCell>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      {direction && (
                                        <DirectionIndicator direction={direction} />
                                      )}
                                      <Typography variant="body2" sx={{ color: "#b5b5b5" }}>
                                        {lastMsgDate}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                )}
                                <TableCell>
                                  <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <ListItemAvatar>
                                      <Avatar
                                        alt={displayName}
                                        src={row["Profile Photo"] || ""}
                                        sx={{
                                          width: 45,
                                          height: 45,
                                          backgroundColor: "#333",
                                          color: "#666",
                                          cursor: "pointer",
                                        }}
                                        onClick={() => window.open(`https://app.gohighlevel.com/v2/location/ZghV6U2wnAogcW6SRaAr/contacts/detail/${row["Contact Id"]}`, '_blank')}
                                      >
                                        {row["Profile Photo"]
                                          ? ""
                                          : row["IG Username"]
                                            ? <PersonIcon />
                                            : <PersonIcon />}
                                      </Avatar>
                                    </ListItemAvatar>
                                    <Box ml={1}>
                                      <Tooltip title={tab === "followers" || tab === "leads" ? displayName : ""}>
                                        <Box ml={1}>
                                          <Tooltip title={tab === "followers" || tab === "leads" ? displayName : ""}>
                                            <Link
                                              component="button"
                                              onClick={() => {
                                                if (tab === "leads") {
                                                  window.open(`https://app.gohighlevel.com/v2/location/ZghV6U2wnAogcW6SRaAr/contacts/detail/${row["Contact Id"]}`, '_blank');
                                                }
                                              }}
                                              underline="hover"
                                              sx={{
                                                fontSize: "16px",
                                                color: isGrayedOut ? "rgba(255, 255, 255, 0.3)" : "#98FF98",
                                                filter: isGrayedOut ? "brightness(0.7)" : "none",
                                                opacity: instagramLinkDisabled ? 0.5 : 1,
                                                textAlign: "left",
                                                cursor: "pointer",
                                              }}
                                            >
                                              {username}
                                            </Link>
                                          </Tooltip>
                                        </Box>
                                      </Tooltip>
                                    </Box>
                                  </Box>
                                </TableCell>

                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: isGrayedOut ? "rgba(255, 255, 255, 0.3)" : "#b5b5b5",
                                      filter: isGrayedOut ? "brightness(0.7)" : "none",
                                    }}
                                  >
                                    {createdDate}
                                  </Typography>
                                </TableCell>

                                <TableCell>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <IconButton
                                      href={!instagramLinkDisabled && hasIGUsername ? profileUrl : undefined}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      aria-label="Instagram Profile"
                                      sx={{
                                        color: isGrayedOut ? "rgba(255, 255, 255, 0.3)" : "#9e9e9e",
                                        filter: isGrayedOut ? "brightness(0.5)" : "none",
                                        marginTop: "-5px",
                                        opacity: instagramLinkDisabled ? 0.5 : 1,
                                      }}
                                      onClick={(e) => {
                                        if (instagramLinkDisabled || !hasIGUsername) {
                                          e.preventDefault();
                                          if (instagramLinkDisabled) {
                                            const currentTime = Date.now();
                                            const secondsRemaining = Math.ceil((7000 - (currentTime - lastClickTime)) / 1000);
                                            setSnackbarMessage(`You must wait ${secondsRemaining} seconds before opening another profile.`);
                                            setSnackbarOpen(true);
                                          }
                                          return;
                                        }
                                        handleLinkClick(e, username, profileUrl);
                                      }}
                                    >
                                      {hasIGUsername ? (
                                        <InstagramIcon
                                          sx={{
                                            color: isGrayedOut ? "rgba(255, 255, 255, 0.3)" : "#98FF98",
                                            fontSize: 28,
                                          }}
                                        />
                                      ) : (
                                        <InstagramIcon sx={{ color: "#777", fontSize: 28 }} />
                                      )}
                                    </IconButton>
                                  </Box>
                                </TableCell>

                                {tab === "leads" && (
                                  <TableCell>
                                    <Tooltip
                                      title={
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleCopy(row.Phone);
                                            }}
                                            sx={{ color: "#98FF98", display: row.Phone ? "flex" : "none" }}
                                          >
                                            <ContentCopyIcon fontSize="small" />
                                          </IconButton>
                                          {row.Phone ? row.Phone : "Phone # Not Collected"}
                                        </Box>
                                      }
                                      slotProps={{
                                        popper: {
                                          modifiers: [
                                            {
                                              name: "preventOverflow",
                                              options: {
                                                boundary: "window",
                                              },
                                            },
                                          ],
                                        },
                                        tooltip: {
                                          sx: {
                                            color: row.Phone ? "#FDFDFD" : "#777",
                                            fontSize: "1.1rem",
                                            padding: "12px 16px",
                                            backgroundColor: "#343434",
                                            borderRadius: "8px",
                                          },
                                        },
                                      }}
                                    >
                                      {row.Phone ? (
                                        <TabletAndroidIcon sx={{ color: "#98FF98", marginLeft: 1.25, fontSize: 24 }} />
                                      ) : (
                                        <TabletAndroidIcon sx={{ color: "#777", marginLeft: 1.25, fontSize: 24 }} />
                                      )}
                                    </Tooltip>
                                  </TableCell>
                                )}

                                {tab === "leads" && (
                                  <TableCell>
                                    <Tooltip
                                      title={
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleCopy(row.Email);
                                            }}
                                            sx={{
                                              color: "#98FF98", display: row.Email && !row.Email.includes("@temp.com") ? "flex" : "none"
                                            }}
                                          >
                                            <ContentCopyIcon fontSize="small" />
                                          </IconButton>
                                          {row.Email && !row.Email.includes("@temp.com") ? row.Email : "Email Not Collected"}
                                        </Box>
                                      }
                                      slotProps={{
                                        popper: {
                                          modifiers: [
                                            {
                                              name: "preventOverflow",
                                              options: {
                                                boundary: "window",
                                              },
                                            },
                                          ],
                                        },
                                        tooltip: {
                                          sx: {
                                            color: row.Email ? "#FDFDFD" : "#777",
                                            fontSize: "1.1rem",
                                            padding: "12px 16px",
                                            backgroundColor: "#343434",
                                            borderRadius: "8px",
                                          },
                                        },
                                      }}
                                    >
                                      {row.Email && !row.Email.includes("@temp.com") ? (
                                        <EmailIcon sx={{ color: "#98FF98", fontSize: 28, marginLeft: 1 }} />
                                      ) : (
                                        <EmailIcon sx={{ color: "#777", fontSize: 28, marginLeft: 1 }} />
                                      )}
                                    </Tooltip>
                                  </TableCell>
                                )}
                              </TableRow>
                            </Fade>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
            </Fade>
          )}
        </Box>
      )}
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>
          Send Message to {currentContact?.["First Name"] || currentContact?.["IG Username"]}
        </DialogTitle>
        <DialogContent>
          {currentConversation ? (
            <>
              <Typography variant="body2" color="textSecondary">
                Last Message: {currentConversation.lastMessageBody}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Date: {new Date(currentConversation.lastMessageDate).toLocaleString()}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No previous messages available for this contact.
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Message"
            fullWidth
            multiline
            rows={4}
            value={messageContent}
            onChange={(e) => setMessageContent(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSendMessage}
            color="primary"
            disabled={loading || !messageContent.trim()}
          >
            {loading ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" variant="filled">
          Message was successfully sent!
        </Alert>
      </Snackbar>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" variant="filled" sx={{
          fontSize: "16px", backgroundColor: "#79c979", border: "1px solid black"
        }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <Dialog open={openPrompt} onClose={() => setOpenPrompt(false)}>
        <DialogTitle>Open in GoHighLevel App?</DialogTitle>
        <DialogContent>
          <Typography>
            Do you want to open this contact in the GoHighLevel app?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePromptApp} sx={{ color: "#98FF98", fontWeight: "bold" }}>
            Open in App
          </Button>
          <Button onClick={handlePromptBrowser} sx={{ color: "#99FF98", }}>
            Open in Browser
          </Button>
          <Button onClick={() => setOpenPrompt(false)} color={"secondary"}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      { }
      {renderAssignmentDialog()}
    </Box>
  );
};

export default Hub;
